import React, { useState, useEffect } from 'react';
import {
    User,
    Mail,
    Calendar,
    Shield,
    Bell,
    ChevronRight,
    Moon,
    Sun,
    LogOut,
    Trash2,
    Home
} from 'lucide-react';
import { Card, Toggle, toast, Button, InputGroup, Input } from '../../components/v2/V2Components';
import ProfileService from '../../services/ProfileService';
import AccountService from '../../services/AccountService';
import PlanUsageCard from '../../components/v2/PlanUsageCard';
import usePageView from '../../hooks/usePageView';
import analytics from '../../services/AnalyticsService';
import { ANALYTICS_EVENTS } from '../../constants/analyticsEvents';

const Profile = ({ darkMode, setDarkMode, onLogout }) => {
    usePageView(ANALYTICS_EVENTS.PROFILE_VIEWED);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [profileData, setProfileData] = useState(null);
    const [notifications, setNotifications] = useState(true);
    // True while the primary-home scoping toggle is persisting. Disables the
    // Toggle so a rapid second flip can't race the in-flight PUT.
    const [savingScope, setSavingScope] = useState(false);

    // Draft state for the First/Last Name inputs. Kept separate from
    // profileData so typing doesn't touch the "saved" values until the PUT
    // actually succeeds — that's also what lets us compute dirty state by
    // diffing against profileData below.
    const [nameForm, setNameForm] = useState({ first_name: '', last_name: '' });
    const [savingName, setSavingName] = useState(false);

    // Account deletion modal state. Kept local — the modal is bespoke
    // because ConfirmationModal doesn't accept children and we need an
    // inline textarea for the optional reason.
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deletingAccount, setDeletingAccount] = useState(false);
    const [deleteReason, setDeleteReason] = useState('');

    const fetchProfileData = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await ProfileService.getProfile();
            setProfileData(data);
            setNameForm({ first_name: data?.first_name || '', last_name: data?.last_name || '' });
        } catch (error) {
            console.error("Error fetching profile data:", error);
            setError(error.response?.data?.message || "Failed to load profile data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProfileData();
    }, []);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // Settings handlers. Each toggle's handler is the single source of
    // truth for that setting — the row container is non-interactive so the
    // Toggle's onChange can't double-fire against a wrapper button.
    // `toggle_state` carries the NEW value (what the user switched it to).
    const handleDarkModeToggle = (next) => {
        setDarkMode(next);
        analytics.track(ANALYTICS_EVENTS.PROFILE_DARK_MODE_TOGGLED, { toggle_state: next });
    };

    const handleNotificationsToggle = (next) => {
        setNotifications(next);
        analytics.track(ANALYTICS_EVENTS.PROFILE_NOTIFICATIONS_TOGGLED, { toggle_state: next });
    };

    // Unlike Dark Mode / Notifications (local-only), this setting persists to
    // the backend because it changes what the items/tasks APIs return. We
    // optimistically flip the local copy, then roll it back if the PUT fails
    // so the Toggle never lies about the saved server state.
    const handleScopeReadsToggle = async (next) => {
        if (savingScope) return;
        setSavingScope(true);
        setProfileData((prev) => ({ ...prev, scope_reads_to_primary_house: next }));
        try {
            await ProfileService.updateProfile({ scope_reads_to_primary_house: next });
            toast.success(next ? 'Now showing your primary home only' : 'Now showing all homes');
        } catch (err) {
            setProfileData((prev) => ({ ...prev, scope_reads_to_primary_house: !next }));
            toast.error(err?.response?.data?.message || 'Failed to update setting');
        } finally {
            setSavingScope(false);
        }
    };

    // True once either name field differs from the last saved value —
    // gates the Save button so it's only actionable when there's something
    // to persist.
    const nameDirty =
        nameForm.first_name !== (profileData?.first_name || '') ||
        nameForm.last_name !== (profileData?.last_name || '');

    const handleNameFieldChange = (field) => (e) => {
        setNameForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

    const handleSaveName = async () => {
        if (!nameDirty || savingName) return;
        setSavingName(true);
        try {
            const updated = await ProfileService.updateProfile({
                first_name: nameForm.first_name,
                last_name: nameForm.last_name,
            });
            setProfileData((prev) => ({ ...prev, ...updated }));
            setNameForm({
                first_name: updated?.first_name || '',
                last_name: updated?.last_name || '',
            });
            analytics.track(ANALYTICS_EVENTS.PROFILE_NAME_UPDATED, {});
            toast.success('Profile updated');
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Failed to update profile');
        } finally {
            setSavingName(false);
        }
    };

    const handlePrivacyClick = () => {
        analytics.track(ANALYTICS_EVENTS.PROFILE_PRIVACY_CLICKED, {});
        // No destination yet — Privacy & Security is not wired up.
    };

    const handleLogout = () => {
        analytics.track(ANALYTICS_EVENTS.PROFILE_SIGN_OUT_CLICKED, {});
        if (onLogout) onLogout();
    };

    const openDeleteModal = () => {
        analytics.track(ANALYTICS_EVENTS.ACCOUNT_DELETE_CLICKED, {});
        setDeleteReason('');
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        setDeletingAccount(true);
        try {
            const result = await AccountService.requestDeletion(deleteReason.trim() || null);
            analytics.track(ANALYTICS_EVENTS.ACCOUNT_DELETE_REQUESTED, {
                grace_period_days: result.grace_period_days,
                had_reason: Boolean(deleteReason.trim()),
            });
            // The grace period does NOT cancel billing (the account is still
            // recoverable); the purge that completes the deletion does. Say so
            // now, while the user is still signed in to read it.
            const billingNote = result.subscription_will_be_canceled
                ? ' Your paid subscription will be canceled when the deletion completes.'
                : '';
            toast.success(
                `Account scheduled for deletion. Sign in within ${result.grace_period_days} day${result.grace_period_days === 1 ? '' : 's'} to cancel.${billingNote}`
            );
            setShowDeleteModal(false);
            if (onLogout) onLogout();
        } catch (err) {
            const message = err?.response?.data?.message || err?.message || 'Failed to schedule account deletion.';
            toast.error(message);
        } finally {
            setDeletingAccount(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--primary)]"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="space-y-4 animate-fade-in">
                <div className="p-6 bg-red-50 border border-red-200 text-red-600 rounded-lg">
                    {error}
                </div>
                <button
                    onClick={fetchProfileData}
                    className="px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition"
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in max-w-2xl mx-auto">
            {/* Profile Header */}
            <div className="text-center mb-8">
                <div className="w-24 h-24 bg-[var(--bg-hover)] rounded-full mx-auto mb-4 flex items-center justify-center text-[var(--primary)] theme-transition border-4 border-[var(--bg-card)] shadow-lg">
                    <User size={48} />
                </div>
                <h2 className="text-2xl font-bold text-[var(--text-main)] theme-transition">
                    {profileData?.first_name && profileData?.last_name
                        ? `${profileData.first_name} ${profileData.last_name}`
                        : profileData?.username || 'User'}
                </h2>
            </div>

            {/* Settings Card */}
            <Card className="divide-y divide-[var(--border)]">
                {/* Toggle rows are non-interactive containers — the Toggle is
                    the sole control. Previously these were <button> wrappers
                    AROUND the Toggle, so a click hit both handlers and the
                    setter fired twice (cancelling itself out). Nesting a
                    <button> in a <button> was also invalid HTML. */}
                <div className="p-4 flex items-center justify-between w-full theme-transition">
                    <div className="flex items-center space-x-3">
                        <div className="p-2 bg-[var(--bg-hover)] rounded-lg text-[var(--text-secondary)] theme-transition">
                            {darkMode ? <Moon size={20} /> : <Sun size={20} />}
                        </div>
                        <div className="text-left">
                            <p className="font-medium text-[var(--text-main)] theme-transition">Dark Mode</p>
                            <p className="text-xs text-[var(--text-secondary)] theme-transition">Adjust appearance for low light</p>
                        </div>
                    </div>
                    <Toggle enabled={darkMode} onChange={handleDarkModeToggle} ariaLabel="Dark mode" />
                </div>

                <div className="p-4 flex items-center justify-between w-full theme-transition">
                    <div className="flex items-center space-x-3">
                        <div className="p-2 bg-[var(--bg-hover)] rounded-lg text-[var(--text-secondary)] theme-transition">
                            <Bell size={20} />
                        </div>
                        <div className="text-left">
                            <p className="font-medium text-[var(--text-main)] theme-transition">Notifications</p>
                            <p className="text-xs text-[var(--text-secondary)] theme-transition">Maintenance alerts & reminders</p>
                        </div>
                    </div>
                    <Toggle enabled={notifications} onChange={handleNotificationsToggle} ariaLabel="Notifications" />
                </div>

                <div className="p-4 flex items-center justify-between w-full theme-transition">
                    <div className="flex items-center space-x-3">
                        <div className="p-2 bg-[var(--bg-hover)] rounded-lg text-[var(--text-secondary)] theme-transition">
                            <Home size={20} />
                        </div>
                        <div className="text-left">
                            <p className="font-medium text-[var(--text-main)] theme-transition">Primary home only</p>
                            <p className="text-xs text-[var(--text-secondary)] theme-transition">Show items & tasks from your primary home only</p>
                        </div>
                    </div>
                    <Toggle
                        enabled={Boolean(profileData?.scope_reads_to_primary_house)}
                        onChange={handleScopeReadsToggle}
                        disabled={savingScope}
                        ariaLabel="Primary home only"
                    />
                </div>

                <button
                    onClick={handlePrivacyClick}
                    className="p-4 flex items-center justify-between w-full hover:bg-[var(--bg-hover)] transition-colors theme-transition text-left"
                >
                    <div className="flex items-center space-x-3">
                        <div className="p-2 bg-[var(--bg-hover)] rounded-lg text-[var(--text-secondary)] theme-transition">
                            <Shield size={20} />
                        </div>
                        <div>
                            <p className="font-medium text-[var(--text-main)] theme-transition">Privacy & Security</p>
                            <p className="text-xs text-[var(--text-secondary)] theme-transition">Manage data sharing</p>
                        </div>
                    </div>
                    <ChevronRight size={18} className="text-[var(--text-muted)] theme-transition" />
                </button>

            </Card>

            {/* Plan & Usage */}
            <PlanUsageCard />

            {/* Personal Information */}
            <Card className="p-6 space-y-4">
                <h3 className="font-bold text-[var(--text-main)] mb-4 theme-transition">Personal Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold mb-1 block theme-transition">
                            Username
                        </label>
                        <input
                            value={profileData?.username || ''}
                            readOnly
                            className="w-full bg-[var(--bg-hover)] border border-[var(--border)] rounded-lg p-3 text-[var(--text-main)] theme-transition cursor-not-allowed"
                        />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold mb-1 block theme-transition">
                            Email
                        </label>
                        <input
                            value={profileData?.email || ''}
                            readOnly
                            className="w-full bg-[var(--bg-hover)] border border-[var(--border)] rounded-lg p-3 text-[var(--text-main)] theme-transition cursor-not-allowed"
                        />
                    </div>
                </div>
                <form
                    aria-label="Personal information"
                    className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4"
                    onSubmit={(event) => {
                        event.preventDefault();
                        handleSaveName();
                    }}
                >
                    <InputGroup label="First Name">
                        <Input
                            value={nameForm.first_name}
                            onChange={handleNameFieldChange('first_name')}
                            disabled={savingName}
                            maxLength={64}
                        />
                    </InputGroup>
                    <InputGroup label="Last Name">
                        <Input
                            value={nameForm.last_name}
                            onChange={handleNameFieldChange('last_name')}
                            disabled={savingName}
                            maxLength={64}
                        />
                    </InputGroup>
                    <div className="md:col-span-2 flex justify-end">
                        <Button
                            type="submit"
                            variant="primary"
                            size="sm"
                            disabled={!nameDirty || savingName}
                            loading={savingName}
                            aria-label={savingName ? 'Saving changes' : 'Save Changes'}
                        >
                            Save Changes
                        </Button>
                    </div>
                </form>
            </Card>

            {/* Account Details */}
            <Card className="p-6">
                <h3 className="font-bold text-[var(--text-main)] mb-4 theme-transition">Account Details</h3>
                <div className="space-y-3">
                    <div className="flex items-center space-x-3 pb-3 border-b border-[var(--border)] theme-transition">
                        <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                            <Calendar size={20} />
                        </div>
                        <div className="flex-1">
                            <p className="text-xs text-[var(--text-secondary)] theme-transition">Member Since</p>
                            <p className="font-medium text-[var(--text-main)] theme-transition">
                                {formatDate(profileData?.date_joined)}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center space-x-3">
                        <div className="p-2 bg-green-50 text-green-600 rounded-lg">
                            <Mail size={20} />
                        </div>
                        <div className="flex-1">
                            <p className="text-xs text-[var(--text-secondary)] theme-transition">Email Status</p>
                            <p className="font-medium text-[var(--text-main)] theme-transition">
                                {profileData?.email ? 'Verified' : 'Not verified'}
                            </p>
                        </div>
                    </div>
                </div>
            </Card>

            {/* Danger Zone — account deletion */}
            <Card className="divide-y divide-[var(--border)]">
                <button
                    onClick={openDeleteModal}
                    className="p-4 flex items-center justify-between w-full hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors theme-transition text-left"
                >
                    <div className="flex items-center space-x-3">
                        {/* Icon container matches the canonical destructive palette
                            used by ConfirmationModal in V2Components.jsx
                            (bg-red-100/red-900-30 + text-red-600/red-400) so all
                            danger icons share the same visual language. */}
                        <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg text-red-600 dark:text-red-400">
                            <Trash2 size={20} />
                        </div>
                        <div>
                            <p className="font-medium text-red-600 dark:text-red-400">Delete Account</p>
                            <p className="text-xs text-[var(--text-secondary)] theme-transition">
                                Permanently remove your account and all data
                            </p>
                        </div>
                    </div>
                    <ChevronRight size={18} className="text-[var(--text-muted)] theme-transition" />
                </button>
            </Card>

            {/* Sign Out Button */}
            <button
                onClick={handleLogout}
                className="w-full p-4 flex items-center justify-center space-x-2 text-red-500 font-medium hover:bg-red-50 rounded-xl transition-colors border-2 border-transparent hover:border-red-200"
            >
                <LogOut size={20} />
                <span>Sign Out</span>
            </button>

            {/* Delete-account modal. Bespoke (not ConfirmationModal) so we can
                embed the optional "reason" textarea. */}
            {showDeleteModal && (
                <div
                    className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in"
                    onClick={() => !deletingAccount && setShowDeleteModal(false)}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="delete-account-title"
                >
                    <div
                        className="bg-[var(--bg-card)] w-full max-w-sm rounded-2xl shadow-xl p-6 theme-transition text-center"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full dark:bg-red-900/30">
                            <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
                        </div>
                        <h3 id="delete-account-title" className="text-xl font-bold text-[var(--text-main)] mb-2 theme-transition">
                            Delete your account?
                        </h3>
                        <p className="text-[var(--text-secondary)] mb-4 theme-transition text-sm">
                            Your account and all your homes, rooms, items, tasks, and uploaded files will be permanently deleted after the grace period.
                            You can cancel anytime before then by signing back in.
                        </p>
                        <textarea
                            value={deleteReason}
                            onChange={(e) => setDeleteReason(e.target.value)}
                            maxLength={255}
                            placeholder="Tell us why you're leaving (optional)"
                            rows={3}
                            disabled={deletingAccount}
                            className="w-full bg-[var(--bg-hover)] border border-[var(--border)] rounded-lg p-3 text-sm text-[var(--text-main)] theme-transition mb-4 resize-none disabled:opacity-50"
                        />
                        <div className="flex space-x-3">
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                disabled={deletingAccount}
                                className="flex-1 py-2 text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] rounded-lg transition theme-transition font-medium disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmDelete}
                                disabled={deletingAccount}
                                className="flex-1 py-2 flex items-center justify-center rounded-lg font-medium text-white transition disabled:opacity-50 bg-red-500 hover:bg-red-600"
                            >
                                {deletingAccount ? 'Deleting...' : 'Delete account'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Profile;
